/**
 * The Football mode's course: a real-scale American football field in feet
 * (Cole, 2026-10-16: "Not a football field overlay. the course needs to be a
 * football field, this will require making a new scene").
 *
 * Coordinates: the scene's own. The ball sits at the line of scrimmage at the
 * origin and every putt rolls toward negative Z, so the offense always attacks
 * the far end zone, away from the camera. One yard is three feet, never scaled
 * to fit the putt: a 45 ft putt covers 15 yards of field. The field is laid
 * out from the offense's side only (`scrimmageYl` counts from the offense's
 * own goal line), so no direction flag is needed; the host maps the engine's
 * possession to the two end zones.
 *
 * Pure data and math, no three.js: the ground shader in SceneContent paints
 * the lines, numbers, end zones and stripes from these constants, and
 * FootballFieldScenery places the goalposts and the stands from this layout.
 */

export const FOOTBALL_FT_PER_YARD = 3;

/**
 * The stadium around the field (the stands and the wall). Build 329 tried it
 * off (the LagKing sky), then the game's random course as the backdrop; Cole
 * chose the stadium again (2026-10-17: "go back to the stadium with the white
 * flag and flagstick"). False brings back the course backdrop.
 */
export const FOOTBALL_STANDS = true;

/**
 * Without the stands, the game's course is the backdrop behind the field
 * (Cole, 2026-10-17: "Can we randomly choose one of our 10 courses as the
 * background?"): the host's random stock course for the session (the same
 * id the phone sends the TV), drawn around the field's middle so nothing of
 * it lands on the field. Null with the stands, or without a course.
 */
export function footballBackdropSceneryId<T extends string>(sceneryId: T | null | undefined): T | null {
  return !FOOTBALL_STANDS && sceneryId ? sceneryId : null;
}
/** 53 1/3 yards sideline to sideline. */
export const FOOTBALL_FIELD_HALF_WIDTH_FT = 80;
export const FOOTBALL_END_ZONE_YARDS = 10;
/** The solid white border outside the sidelines and end lines. */
export const FOOTBALL_BORDER_FT = 6;
/** NFL inbound (hash) lines: 70 ft 9 in from each sideline, 18 ft 6 in apart. */
export const FOOTBALL_HASH_INNER_FT = 9.25;
export const FOOTBALL_HASH_LENGTH_FT = 2;
/** Yard numbers: 6 ft tall, 4 ft wide, tops 9 yards in from the sideline. */
export const FOOTBALL_NUMBER_TOP_FROM_SIDELINE_FT = 27;
export const FOOTBALL_NUMBER_HEIGHT_FT = 6;
export const FOOTBALL_NUMBER_WIDTH_FT = 4;
/** Goalposts: crossbar 10 ft up, uprights 18 ft 6 in apart rising 35 ft above it. */
export const FOOTBALL_CROSSBAR_HEIGHT_FT = 10;
export const FOOTBALL_UPRIGHT_SPACING_FT = 18.5;
export const FOOTBALL_UPRIGHT_HEIGHT_FT = 35;
/**
 * A goal line at or inside this distance counts as "in view" from the
 * address camera: the fog starts at 150 ft, and the Hail Mary cup is at 60.
 */
export const FOOTBALL_END_ZONE_VIEW_FT = 150;
/** Painted end-zone names: letters the scene's label font can draw. */
export const FOOTBALL_END_ZONE_NAME_MAX = 14;

export const FOOTBALL_DEFAULT_AHEAD_COLOR = '#1F3A93';
export const FOOTBALL_DEFAULT_BEHIND_COLOR = '#8C1D18';

/** One end zone's paint. */
export interface PuttSceneEndZone {
  /** Fill color, `#RRGGBB`. */
  color: string;
  /** Optional name painted across the end zone (A-Z, 0-9, space; uppercased,
   *  up to 14 characters). Null or omitted: color only. */
  name?: string | null;
}

/**
 * The Football scene input (SceneContentProps.footballField, PuttScene's
 * `footballField`, the TV scene's `footballField`). Null or absent: the normal
 * green, untouched.
 */
export interface PuttSceneFootballField {
  /** The line of scrimmage, in yards from the offense's own goal line
   *  (0-100). The ball sits on it. The engine's `yl`. */
  scrimmageYl: number;
  /** The first-down line in the same yards, painted yellow. Null when it is
   *  goal to go (the line would be the goal line) or there is no line to make
   *  (a two-point try, a field goal). */
  firstDownYl: number | null;
  /** The end zone the offense is attacking (ahead of the ball): the
   *  defense's color and name. */
  aheadEndZone: PuttSceneEndZone;
  /** The offense's own end zone (behind the ball). */
  behindEndZone: PuttSceneEndZone;
  /** Where the field is laid out from (the yard line at the ball), when it
   *  is not the line of scrimmage: after a play the field holds still at the
   *  old spot while the blue and yellow lines move to their new places
   *  (Football transitions, Cole 2026-10-17). Omitted: the scrimmage line. */
  frameYl?: number;
  /** The camera eases out to a slightly wider view (after a play, while the
   *  lines move); false or omitted eases it back in. */
  wide?: boolean;
  /**
   * A kid's turn (app kidDistances.ts, Cole 2026-10-28): the field is drawn
   * shrunk so a kid's real putt finishes where the engine scores it. Ahead
   * of the ball the first `kidNearFt` feet stand for `kidNearAdultFt` field
   * feet, and every foot past that for 1 / `kidScale` (sideways too: one
   * kid foot is 1 / kidScale field feet). Omitted: the real-scale field.
   */
  kidNearFt?: number;
  kidNearAdultFt?: number;
  kidScale?: number;
}

/** The kid field's three numbers, or null for the real-scale field. */
export function footballKidField(field: Pick<PuttSceneFootballField, 'kidNearFt' | 'kidNearAdultFt' | 'kidScale'>): { nearFt: number; nearAdultFt: number; scale: number } | null {
  const { kidNearFt: nearFt, kidNearAdultFt: nearAdultFt, kidScale: scale } = field;
  if (typeof nearFt !== 'number' || typeof nearAdultFt !== 'number' || typeof scale !== 'number') return null;
  if (!(nearFt > 0 && nearAdultFt > 0 && scale > 0 && scale < 1) || !Number.isFinite(nearFt + nearAdultFt + scale)) return null;
  return { nearFt, nearAdultFt, scale };
}

/**
 * Field feet ahead of the ball for a distance drawn `drawnFt` ahead on a kid
 * field (the shader's mapping); the identity on the real field.
 */
export function footballFieldFtAhead(field: Pick<PuttSceneFootballField, 'kidNearFt' | 'kidNearAdultFt' | 'kidScale'>, drawnFt: number): number {
  const kid = footballKidField(field);
  if (!kid) return drawnFt;
  return drawnFt <= kid.nearFt ? drawnFt * (kid.nearAdultFt / kid.nearFt) : kid.nearAdultFt + (drawnFt - kid.nearFt) / kid.scale;
}

/**
 * The group transform that shrinks the posts and stands onto a kid field:
 * the far scale everywhere, moved so a field point past the near stretch
 * stands where the paint draws it. Empty on the real field.
 */
export function footballKidSceneryTransform(field: Pick<PuttSceneFootballField, 'kidNearFt' | 'kidNearAdultFt' | 'kidScale'>): { scale?: [number, number, number]; position?: [number, number, number] } {
  const kid = footballKidField(field);
  if (!kid) return {};
  return { scale: [kid.scale, kid.scale, kid.scale], position: [0, 0, -(kid.nearFt - kid.nearAdultFt * kid.scale)] };
}

const HEX = /^#[0-9a-fA-F]{6}$/;

function normalizeColor(value: unknown, fallback: string): string {
  if (typeof value !== 'string') return fallback;
  if (HEX.test(value)) return value.toUpperCase();
  // #RGB shorthand, as a host may write white.
  if (/^#[0-9a-fA-F]{3}$/.test(value)) {
    return `#${value[1]}${value[1]}${value[2]}${value[2]}${value[3]}${value[3]}`.toUpperCase();
  }
  return fallback;
}

/** Uppercase A-Z, 0-9 and single spaces, trimmed and capped; empty: null. */
export function footballEndZoneName(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const cleaned = value
    .toUpperCase()
    .replace(/[^A-Z0-9 ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, FOOTBALL_END_ZONE_NAME_MAX)
    .trim();
  return cleaned.length > 0 ? cleaned : null;
}

function normalizeEndZone(value: unknown, fallbackColor: string): PuttSceneEndZone {
  const zone = value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  return { color: normalizeColor(zone.color, fallbackColor), name: footballEndZoneName(zone.name) };
}

/**
 * Accept a field from a host or from the wire (the TV reads it out of the
 * phone's scene payload). Anything malformed is the normal green (null); a
 * scrimmage outside the field is clamped onto it; a first-down line that is
 * not ahead of the ball, or is at or past the goal line, is dropped (goal to go).
 */
export function normalizeFootballField(value: unknown): PuttSceneFootballField | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  const scrimmage = raw.scrimmageYl;
  if (typeof scrimmage !== 'number' || !Number.isFinite(scrimmage)) return null;
  const scrimmageYl = Math.min(100, Math.max(0, scrimmage));
  const first = raw.firstDownYl;
  const firstDownYl =
    typeof first === 'number' && Number.isFinite(first) && first > scrimmageYl && first < 100 ? first : null;
  const frame = raw.frameYl;
  const frameYl = typeof frame === 'number' && Number.isFinite(frame) ? Math.min(100, Math.max(0, frame)) : null;
  const kid = footballKidField(raw as Pick<PuttSceneFootballField, 'kidNearFt' | 'kidNearAdultFt' | 'kidScale'>);
  return {
    scrimmageYl,
    firstDownYl,
    aheadEndZone: normalizeEndZone(raw.aheadEndZone, FOOTBALL_DEFAULT_AHEAD_COLOR),
    behindEndZone: normalizeEndZone(raw.behindEndZone, FOOTBALL_DEFAULT_BEHIND_COLOR),
    ...(frameYl !== null && frameYl !== scrimmageYl ? { frameYl } : {}),
    ...(raw.wide === true ? { wide: true } : {}),
    ...(kid ? { kidNearFt: kid.nearFt, kidNearAdultFt: kid.nearAdultFt, kidScale: kid.scale } : {}),
  };
}

/** The yard line the field is laid out from: the ball's spot. */
export function footballFrameYl(field: PuttSceneFootballField): number {
  return field.frameYl ?? field.scrimmageYl;
}

/** A stable string for memo keys: equal fields give equal keys. */
export function footballFieldKey(field: PuttSceneFootballField | null): string {
  if (!field) return '';
  return [
    field.scrimmageYl,
    field.firstDownYl ?? '',
    field.frameYl ?? '',
    field.aheadEndZone.color,
    field.aheadEndZone.name ?? '',
    field.behindEndZone.color,
    field.behindEndZone.name ?? '',
    field.kidNearFt ?? '',
    field.kidNearAdultFt ?? '',
    field.kidScale ?? '',
  ].join('|');
}

/**
 * Scene Z (ft) of a yard line given in yards from the offense's own goal
 * line: 0 at the line of scrimmage, negative ahead (toward the goal being
 * attacked), positive behind the ball.
 */
export function footballYardLineZ(scrimmageYl: number, yl: number): number {
  return (scrimmageYl - yl) * FOOTBALL_FT_PER_YARD;
}

/** The painted number at a yard line (10, 20 ... 50 ... 20, 10), or null. */
export function footballYardNumber(yl: number): number | null {
  if (!Number.isInteger(yl) || yl % 10 !== 0 || yl <= 0 || yl >= 100) return null;
  return Math.min(yl, 100 - yl);
}

export interface FootballYardLine {
  /** Yards from the offense's own goal line (0 and 100 are the goal lines). */
  yl: number;
  /** Scene Z, ft. */
  z: number;
  number: number | null;
}

export interface FootballFieldLayout {
  /** The blue line: 0 (the ball is on it) unless the field is framed from
   *  another spot while the lines move. */
  scrimmageZ: number;
  /** The yellow line, or null. */
  firstDownZ: number | null;
  /** The goal line being attacked and the end line behind its end zone. */
  aheadGoalZ: number;
  aheadEndLineZ: number;
  /** The offense's own goal line and end line, behind the ball. */
  behindGoalZ: number;
  behindEndLineZ: number;
  /** Midfield (the 50), which the stands are centered on. */
  fieldCenterZ: number;
  /** Feet from the ball to the goal line being attacked. */
  aheadGoalDistanceFt: number;
  /** The attacked end zone starts within FOOTBALL_END_ZONE_VIEW_FT of the ball. */
  aheadEndZoneInView: boolean;
  /** Every 5-yard line, goal line to goal line. */
  yardLines: FootballYardLine[];
}

export function footballFieldLayout(field: PuttSceneFootballField): FootballFieldLayout {
  const s = footballFrameYl(field);
  const z = (yl: number) => footballYardLineZ(s, yl);
  const yardLines: FootballYardLine[] = [];
  for (let yl = 0; yl <= 100; yl += 5) yardLines.push({ yl, z: z(yl), number: footballYardNumber(yl) });
  const aheadGoalDistanceFt = (100 - s) * FOOTBALL_FT_PER_YARD;
  return {
    scrimmageZ: z(field.scrimmageYl),
    firstDownZ: field.firstDownYl == null ? null : z(field.firstDownYl),
    aheadGoalZ: z(100),
    aheadEndLineZ: z(100 + FOOTBALL_END_ZONE_YARDS),
    behindGoalZ: z(0),
    behindEndLineZ: z(-FOOTBALL_END_ZONE_YARDS),
    fieldCenterZ: z(50),
    aheadGoalDistanceFt,
    aheadEndZoneInView: aheadGoalDistanceFt <= FOOTBALL_END_ZONE_VIEW_FT,
    yardLines,
  };
}

/**
 * Where a putt of `distanceFt` finishes on the field, in yards from the
 * offense's own goal line (past 100 is in the end zone). Display math only;
 * the engine scores the putt.
 */
export function footballYardLineAtDistance(scrimmageYl: number, distanceFt: number): number {
  return scrimmageYl + distanceFt / FOOTBALL_FT_PER_YARD;
}

/** The numbers the ground shader reads (ft from the offense's own goal line). */
export interface FootballFieldPaint {
  /** The yard line at the ball (the field's origin), ft. */
  originFt: number;
  /** The blue line of scrimmage, ft. */
  scrimmageFt: number;
  /** -1e4 when there is no first-down line. */
  firstDownFt: number;
}

export const FOOTBALL_NO_LINE_FT = -1e4;

export function footballFieldPaint(field: PuttSceneFootballField): FootballFieldPaint {
  return {
    originFt: footballFrameYl(field) * FOOTBALL_FT_PER_YARD,
    scrimmageFt: field.scrimmageYl * FOOTBALL_FT_PER_YARD,
    firstDownFt: field.firstDownYl == null ? FOOTBALL_NO_LINE_FT : field.firstDownYl * FOOTBALL_FT_PER_YARD,
  };
}

/**
 * The painted name's size in feet inside a 30 ft end zone, keeping the
 * texture's aspect: up to 15 ft tall and 120 ft wide.
 */
export function footballEndZoneNameSizeFt(aspect: number): { widthFt: number; heightFt: number } {
  const a = Number.isFinite(aspect) && aspect > 0 ? aspect : 1;
  const maxW = 120;
  const maxH = 15;
  const heightFt = Math.min(maxH, maxW / a);
  return { widthFt: heightFt * a, heightFt };
}

// ---------------------------------------------------------------------------
// Ground shader paint. `courseGround` (fixed course space, ft) and `map` are
// in scope from SceneContent's ground shader. Every distance is in feet from
// the offense's own goal line ("along") and from the field's centerline
// ("lat"). Lines are antialiased with screen-space derivatives and never
// thinner than about a pixel, so a 4 in line still reads at 0.35 scale on an
// Apple TV instead of breaking into dashes.
// ---------------------------------------------------------------------------

export const FOOTBALL_FIELD_GLSL_UNIFORMS = [
  'uniform vec4 uFbField;', // x origin (ball) ft, y first-down ft, z scrimmage ft, w unused
  'uniform vec3 uFbTurfA;',
  'uniform vec3 uFbTurfB;',
  'uniform vec3 uFbApron;',
  'uniform vec3 uFbPaint;',
  'uniform vec3 uFbYellow;',
  'uniform vec3 uFbScrimmage;',
  'uniform vec3 uFbEndAhead;',
  'uniform vec3 uFbEndBehind;',
  'uniform vec3 uFbInkAhead;',
  'uniform vec3 uFbInkBehind;',
  'uniform sampler2D uFbDigits;',
  'uniform vec4 uFbDigitCell;', // x cell width, y glyph width, z left pad (all u), w glyph height (v)
  'uniform sampler2D uFbNameAhead;',
  'uniform sampler2D uFbNameBehind;',
  'uniform vec4 uFbNameSize;', // ahead w, h; behind w, h (ft); 0 = no name
  // Names and numbers are distance fields (FootballFieldScenery fieldSdfFromAlpha):
  // x, y the ahead and behind names' full field range in ft, z the numbers', w the numbers' bottom pad (v).
  'uniform vec4 uFbSdf;',
  // A kid's field (footballFieldFtAhead): x near drawn ft, y the field ft they
  // stand for, z field-to-drawn scale past them, w 1 = on.
  'uniform vec4 uFbKid;',
  // A distance field's coverage: the 0.5 edge with a band of about one pixel
  // (fw ft a pixel), so edges stay crisp up close and soften far away.
  'float fbInk( float d, float rangeFt, float fw ) {',
  '  float k = clamp( 0.7 * fw / max( rangeFt, 1e-4 ), 0.002, 0.5 );',
  '  return smoothstep( 0.5 - k, 0.5 + k, d );',
  '}',
  'float fbBox( float x, float lo, float hi, float fw ) {',
  '  return smoothstep( lo - fw, lo + fw, x ) * ( 1.0 - smoothstep( hi - fw, hi + fw, x ) );',
  '}',
  // A line of half-width h at distance d, never thinner than ~1 px; lines
  // narrower than a pixel fade instead of shimmering.
  'float fbLine( float d, float h, float fw, float minPx ) {',
  '  float w = max( h, fw * minPx );',
  '  float a = 1.0 - smoothstep( w - fw * 0.5, w + fw * 0.5, d );',
  '  return a * mix( 1.0, h / w, 0.55 );',
  '}',
  // Same, but a marker line that must always read at full strength.
  'float fbMarker( float d, float h, float fw, float minPx ) {',
  '  float w = max( h, fw * minPx );',
  '  return 1.0 - smoothstep( w - fw * 0.5, w + fw * 0.5, d );',
  '}',
].join('\n');

export const FOOTBALL_FIELD_GLSL_PAINT = [
  '{',
  '  float fbGrain = clamp( dot( diffuseColor.rgb, vec3( 0.3333 ) ) / 0.31, 0.75, 1.25 );',
  '  float fbMacro = mix( 0.95, 1.05, texture2D( map, courseGround * 0.013 ).g );',
  '  float fbAhead = -courseGround.y;',
  '  float lat = courseGround.x;',
  '  if ( uFbKid.w > 0.5 ) {',
  '    fbAhead = fbAhead <= uFbKid.x ? fbAhead * uFbKid.y / uFbKid.x : uFbKid.y + ( fbAhead - uFbKid.x ) / uFbKid.z;',
  '    lat = lat / uFbKid.z;',
  '  }',
  '  float along = uFbField.x + fbAhead;',
  '  float alat = abs( lat );',
  '  float fwA = max( fwidth( along ), 1e-4 );',
  '  float fwL = max( fwidth( lat ), 1e-4 );',
  '  float fwInk = sqrt( fwA * fwL );',
  // Zones: apron outside, the field of play in mowed 5-yard bands, end zones.
  '  float inPlay = step( alat, 80.0 ) * step( -30.0, along ) * step( along, 330.0 );',
  '  float bandIx = mod( floor( along / 15.0 ), 2.0 );',
  '  vec3 paint = mix( uFbApron, mix( uFbTurfA, uFbTurfB, bandIx ), inPlay );',
  '  float inLat = step( alat, 80.0 );',
  '  paint = mix( paint, uFbEndAhead, inLat * step( 300.0, along ) * step( along, 330.0 ) );',
  '  paint = mix( paint, uFbEndBehind, inLat * step( -30.0, along ) * step( along, 0.0 ) );',
  // Painted end-zone names, bottoms toward their goal lines (they read from the field).
  '  float inkA = 0.0;',
  '  float inkB = 0.0;',
  '  if ( uFbNameSize.x > 0.0 ) {',
  '    vec2 nuv = vec2( ( lat + uFbNameSize.x * 0.5 ) / uFbNameSize.x, ( along - 315.0 + uFbNameSize.y * 0.5 ) / uFbNameSize.y );',
  '    float inside = step( 0.0, nuv.x ) * step( nuv.x, 1.0 ) * step( 0.0, nuv.y ) * step( nuv.y, 1.0 );',
  '    inkA = inside * fbInk( texture2D( uFbNameAhead, clamp( nuv, 0.0, 1.0 ) ).a, uFbSdf.x, fwInk );',
  '  }',
  '  if ( uFbNameSize.z > 0.0 ) {',
  '    vec2 nuv = vec2( ( -lat + uFbNameSize.z * 0.5 ) / uFbNameSize.z, ( -15.0 - along + uFbNameSize.w * 0.5 ) / uFbNameSize.w );',
  '    float inside = step( 0.0, nuv.x ) * step( nuv.x, 1.0 ) * step( 0.0, nuv.y ) * step( nuv.y, 1.0 );',
  '    inkB = inside * fbInk( texture2D( uFbNameBehind, clamp( nuv, 0.0, 1.0 ) ).a, uFbSdf.y, fwInk );',
  '  }',
  '  paint = mix( paint, uFbInkAhead, inkA * 0.92 );',
  '  paint = mix( paint, uFbInkBehind, inkB * 0.92 );',
  '  float white = 0.0;',
  // The 6 ft border around the whole field.
  '  float inBorderLen = fbBox( along, -36.0, 336.0, fwA );',
  '  white = max( white, fbBox( alat, 80.0, 86.0, fwL ) * inBorderLen );',
  '  white = max( white, ( fbBox( along, -36.0, -30.0, fwA ) + fbBox( along, 330.0, 336.0, fwA ) ) * fbBox( alat, -1.0, 86.0, fwL ) );',
  // Yard lines every 5 yards (4 in), goal lines 8 in, sideline to sideline.
  '  float fiveFt = floor( along / 15.0 + 0.5 ) * 15.0;',
  '  float isGoal = step( abs( fiveFt ), 0.5 ) + step( abs( fiveFt - 300.0 ), 0.5 );',
  '  float yardHalf = mix( 0.1667, 0.3333, isGoal );',
  '  white = max( white, fbLine( abs( along - fiveFt ), yardHalf, fwA, 0.8 ) * step( alat, 80.0 ) * step( -1.0, along ) * step( along, 301.0 ) );',
  // One-yard hash marks at the inbound lines and along the sidelines.
  '  float yardFt = floor( along / 3.0 + 0.5 ) * 3.0;',
  '  float notFive = step( 0.5, abs( yardFt - fiveFt ) );',
  '  float hashLat = fbBox( alat, 9.25, 11.25, fwL ) + fbBox( alat, 77.67, 79.67, fwL );',
  '  white = max( white, fbLine( abs( along - yardFt ), 0.1667, fwA, 0.8 ) * hashLat * notFive * step( 1.0, along ) * step( along, 299.0 ) );',
  // The try line at each 2-yard line.
  '  float tryD = min( abs( along - 6.0 ), abs( along - 294.0 ) );',
  '  white = max( white, fbLine( tryD, 0.1667, fwA, 0.8 ) * fbBox( alat, -1.0, 1.5, fwL ) );',
  // Yard numbers, read from the near sideline, straddling every 10-yard line.
  '  float tenFt = floor( along / 30.0 + 0.5 ) * 30.0;',
  '  if ( tenFt >= 29.0 && tenFt <= 271.0 && alat > 52.0 && alat < 60.0 ) {',
  '    float tens = min( tenFt, 300.0 - tenFt ) / 30.0;',
  '    float r = ( along - tenFt ) * sign( lat );',
  '    float v = ( 59.0 - alat ) / 6.0;',
  '    float digit = r < 0.0 ? tens : 0.0;',
  '    float u = r < 0.0 ? ( r + 5.0 ) / 4.0 : ( r - 1.0 ) / 4.0;',
  '    float inGlyph = step( 0.0, u ) * step( u, 1.0 ) * step( 0.0, v ) * step( v, 1.0 );',
  '    vec2 auv = vec2( digit * uFbDigitCell.x + uFbDigitCell.z + clamp( u, 0.0, 1.0 ) * uFbDigitCell.y,',
  '                     uFbSdf.w + clamp( v, 0.0, 1.0 ) * uFbDigitCell.w );',
  '    white = max( white, inGlyph * fbInk( texture2D( uFbDigits, auv ).a, uFbSdf.z, fwInk ) );',
  // The arrow beside each number but the 50, pointing to the nearer goal.
  '    if ( abs( tenFt - 150.0 ) > 1.0 ) {',
  '      float g = tenFt < 150.0 ? -1.0 : 1.0;',
  '      float a = ( along - ( tenFt + g * 6.0 ) ) * g;',
  '      float l = abs( alat - 53.75 );',
  '      white = max( white, fbBox( a, -0.75, 0.75, fwA ) * ( 1.0 - smoothstep( -fwL, fwL, l - ( 0.75 - a ) ) ) );',
  '    }',
  '  }',
  '  paint = mix( paint, uFbPaint, clamp( white, 0.0, 1.0 ) * 0.94 );',
  // Broadcast lines: the line of scrimmage (blue) and the first-down line (yellow).
  '  float fieldSpan = step( alat, 80.0 );',
  '  paint = mix( paint, uFbScrimmage, fbMarker( abs( along - uFbField.z ), 0.15, fwA, 1.2 ) * fieldSpan * 0.8 );',
  '  paint = mix( paint, uFbYellow, fbMarker( abs( along - uFbField.y ), 0.5, fwA, 2.2 ) * fieldSpan );',
  '  diffuseColor.rgb = paint * fbGrain * fbMacro;',
  '}',
].join('\n');

const FOOTBALL_TURF_COLORS_PAINT = '#F2F4EE';

/** The fill an end zone takes when its team color is too light to carry
 *  white paint (a white team): dark navy, with the name in the team color. */
export const FOOTBALL_LIGHT_TEAM_FILL = '#22324D';

function srgbToLinear(c: number): number {
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

/** WCAG relative luminance of a #RRGGBB color, 0 (black) to 1 (white). */
export function footballColorLuminance(hex: string): number {
  const n = Number.parseInt(hex.slice(1), 16);
  const r = srgbToLinear(((n >> 16) & 255) / 255);
  const g = srgbToLinear(((n >> 8) & 255) / 255);
  const b = srgbToLinear((n & 255) / 255);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * How an end zone is painted from its team color: the color as the fill
 * with a white name, or, for a light color the white lines and lettering
 * would vanish into, a dark fill with the name in the team color.
 */
export function footballEndZonePaint(color: string): { fill: string; ink: string } {
  return footballColorLuminance(color) > 0.45
    ? { fill: FOOTBALL_LIGHT_TEAM_FILL, ink: color }
    : { fill: color, ink: FOOTBALL_TURF_COLORS_PAINT };
}


/** sRGB hex colors of the turf and paint (converted to linear at upload). */
export const FOOTBALL_TURF_COLORS = {
  turfA: '#4B8C38',
  turfB: '#3F7C2F',
  apron: '#356A29',
  paint: FOOTBALL_TURF_COLORS_PAINT,
  yellow: '#F7D417',
  scrimmage: '#2E6CF0',
} as const;

/** The football camera sits higher and farther back than the golf eye
 *  (2.5 ft up, 7 ft behind) and looks a little lower, so the yard lines, the
 *  first-down line and the cups read as a field instead of a sliver: 5 ft up,
 *  11 ft behind the ball at address. */
export const FOOTBALL_CAMERA_LIFT_FT = 2.5;
export const FOOTBALL_CAMERA_PULLBACK_FT = 4;
export const FOOTBALL_CAMERA_LOOK_DROP_FT = 2;

/** The slight zoom out after a play (Cole, 2026-10-17: "ball comes to rest,
 *  slight zoom out, line of scrimmage and first down moves"): the eye rises
 *  and backs off a little and looks a little farther down the field, so the
 *  lines' new spots are in view. Eased over FOOTBALL_CAMERA_WIDE_MS. */
export const FOOTBALL_CAMERA_WIDE_LIFT_FT = 3.5;
export const FOOTBALL_CAMERA_WIDE_PULLBACK_FT = 9;
export const FOOTBALL_CAMERA_WIDE_LOOK_AHEAD_FT = 6;
export const FOOTBALL_CAMERA_WIDE_MS = 900;

/**
 * A golf camera frame as the football camera sees it. `address` (0-1) blends
 * in the raised football eye, which belongs to the address view between
 * putts only: during a putt the follow is the golf chase every other mode
 * uses (Cole, 2026-10-17: "I don't like the overhead follow camera in
 * football; it should be the same follow angle as other modes"). `wide`
 * (0-1, already eased) blends in the after-play zoom out.
 */
export function footballCameraFrame<T extends { pos: [number, number, number]; look: [number, number, number]; fov: number }>(
  frame: T,
  wide = 0,
  address = 1,
): T {
  const unit = (v: number) => Math.max(0, Math.min(1, Number.isFinite(v) ? v : 0));
  const w = unit(wide);
  const a = unit(address);
  return {
    ...frame,
    pos: [
      frame.pos[0],
      frame.pos[1] + FOOTBALL_CAMERA_LIFT_FT * a + FOOTBALL_CAMERA_WIDE_LIFT_FT * w,
      frame.pos[2] + FOOTBALL_CAMERA_PULLBACK_FT * a + FOOTBALL_CAMERA_WIDE_PULLBACK_FT * w,
    ],
    look: [frame.look[0], frame.look[1] - FOOTBALL_CAMERA_LOOK_DROP_FT * a, frame.look[2] - FOOTBALL_CAMERA_WIDE_LOOK_AHEAD_FT * w],
  };
}

/**
 * The address view on a kid's turn (Cole, 2026-10-29: kid mode looked down on
 * the field from overhead; "should have same angle as adult"). The kid field is
 * the adult field drawn small around the ball, so the adult's address camera,
 * aimed at the field feet the kid's putt stands for and shrunk around the ball
 * by the same near scale, sees the same picture: same angle, same lens.
 * The real-scale field returns the frame untouched.
 */
export function footballKidAddressFrame<T extends { pos: [number, number, number]; look: [number, number, number]; fov: number }>(
  frame: T,
  field: Pick<PuttSceneFootballField, 'kidNearFt' | 'kidNearAdultFt' | 'kidScale'> | null,
): T {
  const kid = field ? footballKidField(field) : null;
  if (!kid) return frame;
  const k = kid.nearFt / kid.nearAdultFt;
  return {
    ...frame,
    pos: [frame.pos[0] * k, frame.pos[1] * k, frame.pos[2] * k],
    look: [frame.look[0] * k, frame.look[1] * k, frame.look[2] * k],
  };
}

// ---------------------------------------------------------------------------
// The field advance (Football transitions, 2026-10-17): after a play the
// field holds its frame while the blue and yellow lines slide to their new
// spots over FOOTBALL_FIELD_ADVANCE_MS; the next snap's frame (and any change
// of ends) is a cut, made under the turn card (footballFieldShouldSlide).
// Pure math, so tests can check it.
// ---------------------------------------------------------------------------

export const FOOTBALL_FIELD_ADVANCE_MS = 1000;

/** What the ground and the stands draw right now (may lag the input during an advance). */
export interface FootballFieldDisplay {
  /** The yard line at the ball. */
  frameYl: number;
  scrimmageYl: number;
  firstDownYl: number | null;
}

export function footballFieldDisplayOf(field: PuttSceneFootballField): FootballFieldDisplay {
  return { frameYl: footballFrameYl(field), scrimmageYl: field.scrimmageYl, firstDownYl: field.firstDownYl };
}

export function footballFieldDisplayEqual(a: FootballFieldDisplay, b: FootballFieldDisplay): boolean {
  return a.frameYl === b.frameYl && a.scrimmageYl === b.scrimmageYl && a.firstDownYl === b.firstDownYl;
}

/** The same side has the ball, attacking the same end zone: the field may slide. */
export function footballFieldSamePossession(a: PuttSceneFootballField | null, b: PuttSceneFootballField | null): boolean {
  if (!a || !b) return false;
  return a.aheadEndZone.color === b.aheadEndZone.color
    && (a.aheadEndZone.name ?? null) === (b.aheadEndZone.name ?? null)
    && a.behindEndZone.color === b.behindEndZone.color
    && (b.behindEndZone.name ?? null) === (a.behindEndZone.name ?? null);
}

/**
 * Whether a change of field slides or cuts. Only the lines slide, on a field
 * that holds still (the after-play move); a new frame (the next snap, set up
 * under its turn card with the ball back on the line) or a change of ends
 * cuts. Cole, 2026-10-17: the field sliding forward and then back read as
 * the same transition played twice.
 */
export function footballFieldShouldSlide(last: PuttSceneFootballField | null, next: PuttSceneFootballField | null): boolean {
  return !!last && !!next && footballFieldSamePossession(last, next) && footballFrameYl(last) === footballFrameYl(next);
}

/** Ease in and out (cubic): a slide that starts and lands softly. */
export function footballAdvanceEase(t: number): number {
  const x = Math.max(0, Math.min(1, t));
  return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
}

/**
 * The field at `elapsedMs` into an advance from `from` to `to`. The yellow
 * line slides with the scrimmage line when both ends have one; otherwise it
 * takes its final place at once (it appears or goes away with the cut).
 */
export function footballFieldTweenAt(
  from: FootballFieldDisplay,
  to: FootballFieldDisplay,
  elapsedMs: number,
  durationMs: number = FOOTBALL_FIELD_ADVANCE_MS,
): FootballFieldDisplay {
  const t = durationMs <= 0 ? 1 : footballAdvanceEase(elapsedMs / durationMs);
  const frameYl = from.frameYl + (to.frameYl - from.frameYl) * t;
  const scrimmageYl = from.scrimmageYl + (to.scrimmageYl - from.scrimmageYl) * t;
  const firstDownYl = from.firstDownYl != null && to.firstDownYl != null
    ? from.firstDownYl + (to.firstDownYl - from.firstDownYl) * t
    : to.firstDownYl;
  return { frameYl, scrimmageYl, firstDownYl };
}
